import React from 'react';
import { withRouter } from 'react-router-dom';

import { IAppPropsWithRoute } from '../../../common/types';
import { makeStyles, MuiThemeProvider } from '@material-ui/core/styles';
import { Paper, Grid } from '@material-ui/core';
import themeDark from '../../shared/themeDark';
import api from '../../../store/api';
import ErrorMessage from '../../shared/messages/ErrorMessage'
import { addTaskForEvent, removeTaskForEvent } from '../../../store/signalr/tasksForEvents';
import eventNames from '../../../store/signalr/eventNames';
import CalloutTableForDriver from './CalloutTableForDriver'
import { IRequestCalloutDTO } from '../calloutRequested/types/IRequestCalloutDTOs';
import IAuthState from '../../../interfaces/IAuthState';
import CalloutStatuses from '../types/CalloutStatuses';

interface AgedCareCalloutsForDriverWidgetProps extends IAppPropsWithRoute {
    selectedLocations: string[]|null,
    authStore: IAuthState,
    finishCallout: any
};

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex', flexDirection: 'column',
    },
    container: {
        minHeight: 'max(100vh - 60px, 700px)',
        backgroundSize: '100%', backgroundRepeat: 'no-repeat', borderRadius: '0',
    },
    bodyContainer: {
        height: '100%', margin: 'auto', width: '100%'
    },
    headingTop: {
        marginTop: 30,
        marginBottom: 20,
        fontSize: 36,
        paddingLeft: 40,
        fontWeight: 900,
        textAlign: 'left'
    }
}));




const componentTaskId = "agedCareCalloutsForDriverWidget";
const AgedCareCalloutsForDriverWidget: React.FC<AgedCareCalloutsForDriverWidgetProps> = ({ 
    selectedLocations, 
    authStore,
    finishCallout }) => {
    const classes = useStyles();
    const [errorMessage, setErrorMessage] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const [calloutList, setCalloutList] = React.useState<IRequestCalloutDTO[]>([]);


    const getCallouts = async (): Promise<void> => {
        setErrorMessage('');
        setLoading(true);
        try {
            const requestBody = {
                locations: selectedLocations?.length ? selectedLocations : null
            };
            const res = await api.getRequestCalloutList(``, requestBody);
            setLoading(false);
            if (!res.data.errorRaised) {
                setCalloutList(res.data?.model);
            } else {
                throw (res.data.errorMessage);
            }
        } catch (error) {
            console.error(error);
            setErrorMessage('Error while getting callouts, try to refresh the page');
            setLoading(false);
        }
    }

    const assignCalloutRequestToUser = async (requestCalloutId: string): Promise<void> => {
        setErrorMessage('');
        setLoading(true);
        try {
            const res = await api.assignCalloutRequestToUser(``, {requestCalloutId});
            setLoading(false);
            if (res.data.hasError) {
                setErrorMessage(res.data.message);
                setLoading(false);
            }
        } catch (error) {
            console.error(error);
            setErrorMessage('Error while assigning callout request');
            setLoading(false);
        }
    }

    const updateRequestCallout = async (requestCalloutId: string, status: number): Promise<void> => {
        setErrorMessage('');
        if(status === CalloutStatuses.teamDeparted){
            finishCallout(requestCalloutId);
            return;
        }
        setLoading(true);
        try {
            const res = await api.updateRequestCallout(``, {requestCalloutId, status});
            setLoading(false);
            if (res.hasError) {
                throw (res.data.message);
            }
        } catch (error) {
            console.error(error);
            setErrorMessage('Error while updating callout request');
            setLoading(false);
        }
    }

    const undoRequestCallout = async (requestCalloutId: string, status: number): Promise<void> => {
        setErrorMessage('');
        setLoading(true);
        try {
            const res = await api.undoRequestCallout(``, {requestCalloutId, status});
            setLoading(false);
            if (res.hasError) {
                throw (res.data.message);
            }
        } catch (error) {
            console.error(error);
            setErrorMessage('Error while undo callout request');
            setLoading(false);
        }
    }

    React.useEffect(() => {
        if(selectedLocations !== null){
            getCallouts();
        }
    }, [selectedLocations]);

    const whenCalloutEventDo = () => {
        getCallouts();
    }


    React.useEffect(() => {
        addTaskForEvent(eventNames.refreshCallouts, componentTaskId, whenCalloutEventDo);
        return () => {
            removeTaskForEvent(eventNames.refreshCallouts, componentTaskId);
        }
    }, [whenCalloutEventDo]);

    const calloutActions = {
        assignCalloutRequestToUser:assignCalloutRequestToUser,
        updateRequestCallout: updateRequestCallout,
        undoRequestCallout: undoRequestCallout
    };

    return (
        <>
            <MuiThemeProvider theme={themeDark}>
                <Paper className={classes.container} square={true}>
                    <div className={classes.bodyContainer}>
                        <Grid container>
                            <Grid item xs={12}>
                                <div className={classes.headingTop}> Medical Response Callouts</div>
                            </Grid>
                            {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
                            <Grid item xs={12}>
                                <CalloutTableForDriver 
                                    calloutList={calloutList}
                                    calloutActions={calloutActions} 
                                    authStore={authStore}
                                />
                            </Grid>
                        </Grid>
                    </div>
                </Paper>
            </MuiThemeProvider>
        </>
    );
}

export default withRouter(AgedCareCalloutsForDriverWidget);